import type { DestFallback, DestInput, InputTrack } from '../api'
import { t } from '../i18n'
import { Note, OptionList, Tag } from '../ui'
import { rich } from './VerticalInput'
import { vertical as tallTrack } from './tracks'

/** The input carries an E1 track taller than wide (a 9:16 track of OBS multitrack). */
export const hasVerticalTrack = (tracks?: InputTrack[]) => !!tracks?.some((x) => x.kind === 'video' && x.index > 0 && tallTrack(x))

/**
 * What an output sends of the pair (mockup C / C2; docs/vertical.md §3.1, §3.2; VT-08, VT-18):
 * «What to send» — Auto / Main stream / Vertical input 9:16, and while it asks for the vertical input
 * «If the vertical input stops» — Switch to your main stream (default) / a 9:16 break screen. A multitrack
 * stream with a 9:16 track picks a track instead (the track lists above) and says so; the vertical half of
 * a YouTube Dual stream always sends the vertical input, so only the fallback is shown.
 */
export function VerticalSource({ platformLabel, portrait, input, fallback, onInput, onFallback, tracks, dual, disabled }: {
  platformLabel: string; portrait: boolean;
  input: DestInput; fallback: DestFallback;
  onInput: (v: DestInput) => void; onFallback: (v: DestFallback) => void;
  tracks?: InputTrack[];
  /** the vertical output of a YouTube Dual stream */
  dual?: boolean;
  disabled?: boolean;
}) {
  if (hasVerticalTrack(tracks)) {
    // VT-18 (frame C2): the track lists decide; the vertical input and "What to send" don't apply
    return <Note data-testid="dest-vertical-tracks">{rich(t('vert.c2.note'))}</Note>
  }
  const wants = input === 'vertical' || (input === 'auto' && portrait)
  return (
    <div data-testid="dest-vertical" data-input={input} data-fallback={fallback} className="flex flex-col gap-3">
      {!dual && (
        <div className="flex flex-col gap-1.5">
          <span id="dest-input-label" className="text-[13px] text-text-soft">{t('vert.dest.input')}</span>
          {/* DS: VT-08 — the description under an available option in the same .why line */}
          <OptionList labelledBy="dest-input-label" value={input} onChange={onInput} disabled={disabled} options={[
            { value: 'auto', label: t('vert.dest.auto'), desc: portrait ? t('vert.dest.autoWhy', { platform: platformLabel }) : undefined, testId: 'dest-input-auto' },
            { value: 'main', label: t('vert.dest.main'), testId: 'dest-input-main' },
            { value: 'vertical', label: t('vert.dest.vertical'), tags: <Tag>{t('vert.tag')}</Tag>, desc: t('vert.dest.verticalWhy'), testId: 'dest-input-vertical' },
          ]} />
        </div>
      )}
      {wants && (
        <div className="flex flex-col gap-1.5">
          <span id="dest-fallback-label" className="text-[13px] text-text-soft">{t('vert.dest.fallback')}</span>
          <OptionList labelledBy="dest-fallback-label" value={fallback} onChange={onFallback} disabled={disabled} options={[
            { value: 'main', label: t('vert.dest.fbMain'), desc: t('vert.dest.fbMainWhy'), testId: 'dest-fallback-main' },
            { value: 'slate', label: t('vert.dest.fbSlate'), tags: <Tag>{t('vert.tag')}</Tag>, desc: t('vert.dest.fbSlateWhy'), testId: 'dest-fallback-slate' },
          ]} />
        </div>
      )}
      {wants && fallback === 'main' && <Note>{rich(t('vert.dest.note'))}</Note>}
    </div>
  )
}
